import CommonLookupDisplay from "components/CommonLookupDisplay";
import { GlobalContext } from "components/GlobalContext";
import { createFilterableArrayColumnDefinition, createFilterableDateRangeColumnDefinition, IColumnDefinition } from "components/SortableTable";
import IEquipment, { IEquipmentCreateOrUpdate, IEquipmentFilters, IEquipmentInitResult } from "models/sql/IEquipment";
import { useContext, useRef, useState } from "react";
import { Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import EquipmentService from "services/EquipmentService";
import { formatDate } from "utilities/StringUtility";
import { applyGlobalFilterData } from "utilities/MiscUtilities";
import { LinkContainer } from "react-router-bootstrap";

interface IEquipmentLookupProps {
    baseData?: IEquipmentInitResult;
}

const COUNTRIES_FILTER_KEY = "countries";
const STATES_FILTER_KEY = "states";

const EquipmentLookup = (props: IEquipmentLookupProps) => {
    const [filter, setFilter] = useState<IEquipmentFilters>({});
    const globalContext = useContext(GlobalContext);
    const globalContextRef = useRef(globalContext);
    globalContextRef.current = globalContext;
    const navigate = useNavigate();

    const exportTableDataAsync = async () => {
        const accessToken = await globalContext.getAccessTokenAsync();
        EquipmentService.DownloadExcelFileAsync(
            accessToken,
            "EquipmentExport.xlsx",
            applyGlobalFilterData(
                STATES_FILTER_KEY,
                COUNTRIES_FILTER_KEY,
                filter,
                globalContextRef.current.region,
                globalContextRef.current.organizationType));
    };

    const getColumns = (): IColumnDefinition<IEquipment, IEquipmentFilters>[] => {
        return [
            createFilterableDateRangeColumnDefinition<IEquipment, IEquipmentFilters>(
                globalContext,
                "created",
                // @ts-ignore
                "created",
                true,
                filter,
                (value, row) => <td className="align-top" key={`created${row.equipmentId}`}>
                    {row.created ? formatDate(new Date(row.created)) : ""}
                </td>),
            createFilterableArrayColumnDefinition<IEquipment, IEquipmentFilters>(
                globalContext,
                // @ts-ignore
                "productName",
                "productNames",
                true,
                filter,
                undefined,
                (value, row) => <td className="align-top" key={`productName${row.equipmentId}`}>
                    {row.product?.name} ({row.product?.partNumber})
                </td>,
                "Product"),
            createFilterableArrayColumnDefinition<IEquipment, IEquipmentFilters>(
                globalContext,
                "serialNumber",
                "serialNumbers",
                true,
                filter,
                undefined,
                (value, row) => <td className="align-top" key={`serialNumber${row.equipmentId}`}>
                    {
                        (row.serialNumber || "").split(",")
                            .map(
                                (e, index, array) => <>{e}{array.length > 1 && <br></br>}</>)
                    }</td>),
            createFilterableArrayColumnDefinition<IEquipment, IEquipmentFilters>(
                globalContext,
                "departmentName",
                "departmentNames",
                true,
                filter,
                undefined,
                (value, row) => <td className="align-top" key={`departmentName${row.equipmentId}`}>
                    {row.fireDepartment?.fireDeptName ?? row.departmentName}</td>,
                "Department"),
            createFilterableArrayColumnDefinition<IEquipment, IEquipmentFilters>(
                globalContext,
                // @ts-ignore
                "dealerName",
                "dealerNames",
                true,
                filter,
                undefined,
                (value, row) => <td className="align-top" key={`dealerName${row.equipmentId}`}>
                    {row.dealer?.dealerName}
                </td>,
                "Dealer"),
            createFilterableDateRangeColumnDefinition<IEquipment, IEquipmentFilters>(
                globalContext,
                "shippedDate",
                // @ts-ignore
                "shippedDate",
                true,
                filter,
                (value, row) => <td className="align-top" key={`shipdate${row.equipmentId}`}>
                    {value ? formatDate(new Date(value)) : ""}
                </td>,
                "Ship Date"),
            createFilterableDateRangeColumnDefinition<IEquipment, IEquipmentFilters>(
                globalContext,
                "registrationDate",
                // @ts-ignore
                "registrationDate",
                true,
                filter,
                (value, row) => <td className="align-top" key={`registrationdate${row.equipmentId}`}>
                    {row.registrationDate ? formatDate(new Date(row.registrationDate)) : ""}
                </td>)
        ];
    };

    const getTools = () => [
        <>
            {
                globalContext.roles.some(r => r === "Web.Tools") &&
                <LinkContainer to="/Tools/EquipmentMassAdd">
                    <Button
                        style={{
                            "width": "130px"
                        }}
                        variant="primary"
                        className="float-start full-sm"
                        onClick={() => {
                        }}>Mass Add</Button>
                </LinkContainer>
            }
        </>,
        <Button
            className="me-2 full-sm"
            key="downloadExcel"
            onClick={() => { exportTableDataAsync(); }}>Export</Button>,
        <>
            {
                globalContext.roles.some(r => r === "Web.Admin") &&
                <Button
                    key="createnew"
                    variant="success"
                    className="full-sm"
                    onClick={() => { navigate(`/Equipment/Detail/`) }}>Create New</Button>
            }
        </>
    ];

    return (
        <CommonLookupDisplay<IEquipmentFilters, IEquipment, IEquipmentCreateOrUpdate>
            baseFilters={{
                sortBys: ["EquipmentId"],
                sortDescending: true
            }}
            filterHints={{
                equipmentIds: "Array",
                dealerIds: "Array",
                departmentNames: "Array",
                departmentIds: "Array",
                stationIds: "Array",
                productIds: "Array",
                parentEquipmentIds: "Array",
                serialNumbers: "Array",
                extraData: "Array",
                sortBys: "Array",
                sortDescending: "Boolean",
                states: "Array",
                countries: "Array",
                repCompanies: "Array",
                productPrefixs: "Array",
                registrationNames: "Array",
                registrationEmails: "Array",
                organizationTypes: "Array",
                stationNumbers: "Array",
                locations: "Array"
            }}
            dataTypeName="Equipment"
            dataTypeNamePlural="Equipment"
            pathName="Equipment"
            primaryIdKey="equipmentId"
            className=""
            countriesFilterKey={COUNTRIES_FILTER_KEY}
            statesFilterKey={STATES_FILTER_KEY}
            filter={filter}
            setFilter={setFilter}
            columnDefinitions={getColumns()}
            service={EquipmentService}
            tools={getTools()}
        />
    );
}

export default EquipmentLookup;
